import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { faSave } from "@fortawesome/pro-solid-svg-icons/faSave";
import { faTimesCircle } from "@fortawesome/pro-solid-svg-icons/faTimesCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Col from "antd/es/col";
import Row from "antd/es/row";
import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import ImportConfig from "@mapmycustomers/shared/types/imports/ImportConfig";
import { Button } from "@mapmycustomers/ui";

import StepsEnum from "@app/scene/settings/component/Imports/enum/Steps";
import {
  clearFilePreview,
  initMatchFieldsStep,
  setStep,
} from "@app/scene/settings/component/Imports/store/actions";
import { getMissedFileColumns } from "@app/scene/settings/component/Imports/store/selectors";
import { RootState } from "@app/store/rootReducer";
import layout from "@app/styles/layout";

import RenameConfigButton from "../RenameConfigButton";

import styles from "./ConfigCard.module.scss";

interface Props {
  clearFilePreview: typeof clearFilePreview;
  config?: ImportConfig;
  initMatchFieldsStep: typeof initMatchFieldsStep;
  missedFileColumns: string[];
  setStep: typeof setStep;
}

const ConfigCard: React.FC<Props> = ({
  clearFilePreview,
  config,
  initMatchFieldsStep,
  missedFileColumns,
  setStep,
}) => {
  const intl = useIntl();

  const handleInitMappingStep = useCallback(() => initMatchFieldsStep(), [initMatchFieldsStep]);
  const formattedMissedColumns: string = useMemo(
    () => missedFileColumns.map((missedFileColumn) => `"${missedFileColumn}"`).join(", "),
    [missedFileColumns]
  );

  const handleChooseAnotherFile = useCallback(() => {
    setStep(StepsEnum.UPLOAD);
    clearFilePreview();
  }, [clearFilePreview, setStep]);

  if (!config) {
    return null;
  }

  return (
    <Row align="middle" className={styles.container} justify="space-between">
      <Col className={styles.info}>
        <Row align="middle" gutter={layout.spacerM}>
          <Col>
            <FontAwesomeIcon className={styles.saveIcon} icon={faSave} />
          </Col>
          <Col className={styles.infoText}>
            {missedFileColumns.length
              ? intl.formatMessage(
                  {
                    id: "settings.newImports.config.bar.title.missedColumns",
                    defaultMessage: `"{name}” is currently applied but <b>your imported file is missing the following columns</b> : {columns}`,
                    description: "Text of import config info block Field matching updated",
                  },
                  { b: (text) => <b>{text}</b>, columns: formattedMissedColumns, name: config.name }
                )
              : intl.formatMessage(
                  {
                    id: "settings.newImports.config.bar.title",
                    defaultMessage: `"{name}” is currently applied`,
                    description: "Text of import config info block Field matching updated",
                  },
                  { name: config.name }
                )}
          </Col>
        </Row>
      </Col>
      <Col>
        <Row align="middle" gutter={layout.spacerM}>
          <Col>
            {missedFileColumns.length ? (
              <Button onClick={handleChooseAnotherFile}>
                {intl.formatMessage({
                  id: "settings.newImports.config.bar.importAnotherFile",
                  defaultMessage: "Import Another File",
                  description: "Text of import config info block import another file",
                })}
              </Button>
            ) : (
              <RenameConfigButton config={config}>
                <span className={styles.editNameLabel}>
                  {intl.formatMessage({
                    id: "settings.newImports.config.bar.renameButton",
                    defaultMessage: "Edit name",
                    description: "Text of import config info block rename config",
                  })}
                </span>
              </RenameConfigButton>
            )}
          </Col>
          <Col>
            {missedFileColumns.length ? (
              <Button onClick={handleInitMappingStep} type="primary">
                {intl.formatMessage({
                  id: "settings.newImports.config.bar.clearButton",
                  defaultMessage: "Start matching from scratch",
                  description: "Text of import config info block Start from scratch button",
                })}
              </Button>
            ) : (
              <Tooltip
                placement="bottom"
                title={intl.formatMessage({
                  id: "settings.newImports.config.bar.clearButton.tooltip",
                  defaultMessage: "Clear saved matching",
                  description: "Text of import config info block clear button tooltip",
                })}
              >
                <FontAwesomeIcon
                  className={styles.clearIcon}
                  icon={faTimesCircle}
                  onClick={handleInitMappingStep}
                />
              </Tooltip>
            )}
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

const mapStateToProps = (state: RootState) => ({
  missedFileColumns: getMissedFileColumns(state),
});

const mapDispatchToProps = {
  clearFilePreview,
  initMatchFieldsStep,
  setStep,
};

export default connect(mapStateToProps, mapDispatchToProps)(ConfigCard);
